// pages/IbptPage.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import MainLayout from "../components/mainLayout";
import { api } from "../api";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import {
  UploadCloud,
  Download,
  RefreshCw,
  HelpCircle,
  FileText,
  CheckCircle2,
  AlertTriangle,
  ClipboardCopy,
  Search,
} from "lucide-react";
import { showError } from "../utils/toast";
import HelpBar from "../components/HelpBar";
import HelpModal from "../components/HelpModal";
import "../sass/pages/ibpt.scss";

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("pt-BR") : "-");
const fmtDateTime = (d) => (d ? new Date(d).toLocaleString("pt-BR") : "-");

// ===== visual-only: força DD/MM/YYYY no input do teste
function formatDDMMYYYYVisual(v) {
  const s = String(v || "").replace(/\D/g, "").slice(0, 8);
  const dd = s.slice(0, 2);
  const mm = s.slice(2, 4);
  const yyyy = s.slice(4, 8);
  let out = dd;
  if (mm) out += "/" + mm;
  if (yyyy) out += "/" + yyyy;
  return out;
}

// converte dd/MM/yyyy -> yyyy-MM-dd (para mandar pro backend se quiser)
function ddmmyyyyToIso(v) {
  const m = String(v || "").trim().match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!m) return null;
  const dd = m[1];
  const mm = m[2];
  const yyyy = m[3];
  return `${yyyy}-${mm}-${dd}`;
}

function StatusTag({ type = "info", children }) {
  const map = {
    info: { bg: "#e0f2fe", fg: "#075985" },
    ok: { bg: "#dcfce7", fg: "#166534" },
    warn: { bg: "#fef9c3", fg: "#854d0e" },
    err: { bg: "#fee2e2", fg: "#991b1b" },
  };
  const c = map[type] || map.info;
  return (
    <span
      style={{
        background: c.bg,
        color: c.fg,
        border: "1px solid rgba(0,0,0,.06)",
        borderRadius: 999,
        fontSize: 12,
        padding: "2px 10px",
        lineHeight: "18px",
        fontWeight: 800,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </span>
  );
}

function fileSizeLabel(bytes) {
  const b = Number(bytes || 0);
  if (!Number.isFinite(b) || b <= 0) return "-";
  const units = ["B", "KB", "MB", "GB"];
  let v = b;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(i === 0 ? 0 : 2)} ${units[i]}`;
}

function cutHash(h, n = 18) {
  const s = String(h || "");
  if (!s) return "-";
  return s.length > n ? s.slice(0, n) + "…" : s;
}

export default function IbptPage() {
  const [helpOpen, setHelpOpen] = useState(false);

  // Versão atual
  const [cur, setCur] = useState(null);
  const [loadingCur, setLoadingCur] = useState(true);
  const [curErr, setCurErr] = useState("");

  // Upload
  const [uploadPor, setUploadPor] = useState("");
  const [obs, setObs] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState("");

  // Download (manual CSV)
  const [downloadInfo, setDownloadInfo] = useState(null);
  const [downloading, setDownloading] = useState(false);

  // Check-linguicao (simulador PDV)
  const [clientVigenciaFim, setClientVigenciaFim] = useState(""); // visual dd/MM/yyyy
  const [clientNcmHash, setClientNcmHash] = useState("");
  const [checking, setChecking] = useState(false);
  const [checkResult, setCheckResult] = useState(null);

  const canSubmit = useMemo(() => {
    if (!file) return false;
    const name = String(file?.name || "").toLowerCase();
    if (!name.endsWith(".csv")) return false;
    return true;
  }, [file]);

  const loadVersaoAtual = useCallback(async () => {
    setLoadingCur(true);
    setCurErr("");
    try {
      const { data } = await api.get("/ibpt/versao-atual");
      setCur(data || null);
    } catch (e) {
      const msg = e?.response?.data?.error || e?.message || "Falha ao buscar versão IBPT.";
      setCurErr(msg);
      showError(msg);
      setCur(null);
    } finally {
      setLoadingCur(false);
    }
  }, []);

  useEffect(() => {
    loadVersaoAtual();
  }, [loadVersaoAtual]);

  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target?.tagName || "").toLowerCase();
      const isField = ["input", "select", "textarea"].includes(tag);
      if (e.key === "?" && !isField) {
        e.preventDefault();
        setHelpOpen((v) => !v);
      }
      if (e.key.toLowerCase() === "r" && !isField) {
        e.preventDefault();
        loadVersaoAtual();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [loadVersaoAtual]);

  const resetForm = () => {
    setUploadPor("");
    setObs("");
    setFile(null);
    setUploadMsg("");
  };

  const handlePickFile = (f) => {
    setUploadMsg("");
    setDownloadInfo(null);
    setCheckResult(null);
    setFile(f || null);
  };

  const doUpload = async (e) => {
    e.preventDefault();
    setUploadMsg("");
    setDownloadInfo(null);

    if (!canSubmit) {
      showError("Selecione um arquivo .CSV válido.");
      return;
    }

    setUploading(true);
    try {
      const fd = new FormData();
      if (uploadPor.trim()) fd.append("uploadPor", uploadPor.trim());
      if (obs.trim()) fd.append("obs", obs.trim());
      fd.append("arquivo", file);

      await api.post("/ibpt/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setUploadMsg("Upload concluído e versão ativada.");
      await loadVersaoAtual();

      // pré-carrega link (manual)
      try {
        const dl = await api.get("/ibpt/download");
        setDownloadInfo(dl.data || null);
      } catch {
        // noop
      }
    } catch (err) {
      const status = err?.response?.status;
      const msg =
        err?.response?.data?.mensagem ||
        err?.response?.data?.error ||
        err?.message ||
        "Falha no upload do IBPT.";
      showError(msg);
      setUploadMsg("");
    } finally {
      setUploading(false);
    }
  };

  const doGetDownload = async () => {
    setDownloading(true);
    setDownloadInfo(null);
    try {
      const { data } = await api.get("/ibpt/download");
      setDownloadInfo(data || null);
      return data;
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || "Falha ao gerar download.";
      showError(msg);
      return null;
    } finally {
      setDownloading(false);
    }
  };

  const openDownload = async () => {
    const info = downloadInfo || (await doGetDownload());
    if (!info?.url) return;
    window.open(info.url, "_blank", "noopener,noreferrer");
  };

  const copyLink = async () => {
    const info = downloadInfo || (await doGetDownload());
    if (!info?.url) return;
    try {
      await navigator.clipboard.writeText(info.url);
      setUploadMsg("Link de download copiado.");
    } catch {
      showError("Falha ao copiar link (permissão do navegador).");
    }
  };

  // ===== novo: check-linguicao (PDV)
  const doCheckLinguicao = async () => {
    setChecking(true);
    setCheckResult(null);
    try {
      const params = {};
      if (clientNcmHash.trim()) params.clientNcmHash = clientNcmHash.trim();

      // visual dd/MM/yyyy => envia iso se estiver completo; senão manda como digitado
      const v = clientVigenciaFim.trim();
      if (v) params.clientVigenciaFim = ddmmyyyyToIso(v) || v;

      const resp = await api.get("/ibpt/check-linguicao", {
        params,
        validateStatus: (s) => (s >= 200 && s < 300) || s === 204,
      });

      if (resp.status === 204) {
        setCheckResult({ shouldDownload: false, reason: "NO_UPDATE (204)" });
        return;
      }

      setCheckResult(resp.data || { shouldDownload: true, reason: "UPDATE_AVAILABLE" });
    } catch (e) {
      const msg = e?.response?.data?.error || e?.message || "Falha ao checar atualização.";
      showError(msg);
      setCheckResult({ error: msg });
    } finally {
      setChecking(false);
    }
  };

  return (
    <MainLayout>
      <div className="ibpt-page">
        {/* ===== Toolbar ===== */}
        <div className="ibpt-toolbar">
          <div className="ibpt-title">
            <div className="h1">
              <FileText size={18} />
              IBPT — Gestão de arquivo
            </div>
            <div className="sub">Upload lê CSV, calcula NCM_HASH e bloqueia por VigênciaFim.</div>
          </div>

          <div className="ibpt-actions">
            <button className="btn ghost" onClick={loadVersaoAtual} title="Atualizar (atalho: r)">
              <RefreshCw size={16} />
              <span>Atualizar</span>
            </button>
            <button className="btn ghost" onClick={() => setHelpOpen(true)} title="Ajuda (atalho: ?)">
              <HelpCircle size={16} />
              <span>Ajuda</span>
            </button>
          </div>
        </div>

        <HelpBar>
          <b>Governança:</b> o upload bloqueia se <i>VigênciaFim</i> do arquivo enviado for <i>inferior</i> à vigente.
          <b> Download:</b> manual (<code>/download</code>) e PDV via NCM String (<code>/check-linguicao</code>, retorna <code>204</code> se não precisa).
          Atalhos: <code>r</code> atualizar, <code>?</code> ajuda.
        </HelpBar>

        <div className="ibpt-grid">
          {/* ===== Versão atual ===== */}
          <div className="card">
            <div className="card-header">
              <div className="card-title">Versão ativa</div>
              <div className="card-right">
                {loadingCur ? (
                  <StatusTag type="info">Carregando</StatusTag>
                ) : cur ? (
                  <StatusTag type="ok">Ativa</StatusTag>
                ) : (
                  <StatusTag type="warn">Nenhuma</StatusTag>
                )}
              </div>
            </div>

            <div className="card-body">
              {loadingCur && (
                <>
                  <Skeleton height={16} width={240} style={{ marginBottom: 10 }} />
                  <Skeleton height={16} width={320} style={{ marginBottom: 10 }} />
                  <Skeleton height={16} width={280} />
                </>
              )}

              {!loadingCur && curErr && (
                <div className="state err">
                  <AlertTriangle size={16} />
                  <span>{curErr}</span>
                </div>
              )}

              {!loadingCur && !curErr && !cur && (
                <div className="state warn">
                  <AlertTriangle size={16} />
                  <span>Sem versão ativa. Faça o primeiro upload.</span>
                </div>
              )}

              {!loadingCur && cur && (
                <div className="kv">
                  <div className="row">
                    <div className="k">VigênciaFim</div>
                    <div className="v">{fmtDate(cur.dtVigenciaFim)}</div>
                  </div>

                  <div className="row">
                    <div className="k">Upload em</div>
                    <div className="v">{fmtDateTime(cur.uploadEm)}</div>
                  </div>

                  <div className="row">
                    <div className="k">Upload por</div>
                    <div className="v">{cur.uploadPor || "-"}</div>
                  </div>

                  <div className="row">
                    <div className="k">Arquivo</div>
                    <div className="v" title={cur.nomeArquivo || ""}>
                      {cur.nomeArquivo || "-"}
                    </div>
                  </div>

                  <div className="row">
                    <div className="k">HASH_ARQ</div>
                    <div className="v mono" title={cur.hashArquivo || ""}>
                      {cutHash(cur.hashArquivo)}
                    </div>
                  </div>

                  <div className="row">
                    <div className="k">NCM_HASH</div>
                    <div className="v mono" title={cur.ncmHash || ""}>
                      {cutHash(cur.ncmHash)}
                    </div>
                  </div>

                  <div className="row">
                    <div className="k">Storage</div>
                    <div className="v mono" title={cur.path || ""}>
                      {cur.bucket ? `${cur.bucket}/` : ""}
                      {cur.path || "-"}
                    </div>
                  </div>

                  <div className="actions-row">
                    <button className="btn primary" onClick={openDownload} disabled={!cur}>
                      <Download size={16} />
                      <span>Baixar CSV ativo</span>
                    </button>

                    <button className="btn ghost" onClick={copyLink} disabled={!cur}>
                      <ClipboardCopy size={16} />
                      <span>Copiar link</span>
                    </button>
                  </div>

                  {downloadInfo?.url && (
                    <div className="hint">
                      <CheckCircle2 size={14} />
                      <span>
                        Link pronto (expira em <b>{downloadInfo.expiraEmSeg || 600}s</b>)
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* ===== Upload ===== */}
          <div className="card">
            <div className="card-header">
              <div className="card-title">Upload (CSV)</div>
              <div className="card-right">
                {uploading ? <StatusTag type="info">Processando</StatusTag> : <StatusTag type="info">Envio</StatusTag>}
              </div>
            </div>

            <div className="card-body">
              <form className="ibpt-form" onSubmit={doUpload}>
                <div className="form-row">
                  <label>
                    uploadPor
                    <input
                      type="text"
                      value={uploadPor}
                      onChange={(e) => setUploadPor(e.target.value)}
                      placeholder="SSW"
                      autoComplete="off"
                      maxLength={80}
                    />
                  </label>

                  <label>
                    observação
                    <input
                      type="text"
                      value={obs}
                      onChange={(e) => setObs(e.target.value)}
                      placeholder="Carga mensal"
                      autoComplete="off"
                      maxLength={500}
                    />
                  </label>
                </div>

                <div className="drop">
                  <input
                    id="ibpt-file"
                    type="file"
                    accept=".csv,text/csv"
                    onChange={(e) => handlePickFile(e.target.files?.[0] || null)}
                  />
                  <label htmlFor="ibpt-file" className={`dropzone ${file ? "has" : ""}`}>
                    <UploadCloud size={18} />
                    <div className="droptext">
                      <div className="t1">{file ? "Arquivo selecionado" : "Selecionar arquivo IBPT (.csv)"}</div>
                      <div className="t2">
                        {file ? (
                          <>
                            <b>{file.name}</b> • {fileSizeLabel(file.size)}
                          </>
                        ) : (
                          "Somente CSV • até 50MB"
                        )}
                      </div>
                      {file && !String(file.name || "").toLowerCase().endsWith(".csv") && (
                        <div className="t3">Arquivo inválido: backend está preparado para CSV.</div>
                      )}
                    </div>
                  </label>
                </div>

                <div className="form-actions">
                  <button className="btn solid" type="submit" disabled={!canSubmit || uploading}>
                    <UploadCloud size={16} />
                    <span>{uploading ? "Enviando..." : "Enviar e ativar"}</span>
                  </button>

                  <button className="btn ghost" type="button" onClick={resetForm} disabled={uploading}>
                    Limpar
                  </button>

                  <button
                    className="btn ghost"
                    type="button"
                    onClick={doGetDownload}
                    disabled={downloading}
                    title="Gerar link signed para download"
                  >
                    <Download size={16} />
                    <span>{downloading ? "Gerando..." : "Gerar link"}</span>
                  </button>
                </div>

                {uploadMsg && (
                  <div className="state ok">
                    <CheckCircle2 size={16} />
                    <span>{uploadMsg}</span>
                  </div>
                )}

                {!uploadMsg && !canSubmit && (
                  <div className="hint subtle">
                    <AlertTriangle size={14} />
                    <span>Requisito: selecionar um arquivo .CSV.</span>
                  </div>
                )}
              </form>
            </div>
          </div>

          {/* ===== Check-linguicao (PDV) ===== */}
          <div className="card ibpt-span">
            <div className="card-header">
              <div className="card-title">Teste do check-linguicao (simulador PDV)</div>
              <div className="card-right">
                {checking ? <StatusTag type="info">Consultando</StatusTag> : <StatusTag type="info">Automático</StatusTag>}
              </div>
            </div>

            <div className="card-body">
              <div className="ibpt-form">
                <div className="form-row">
                  <label>
                    clientVigenciaFim (DD/MM/YYYY) — visual
                    <input
                      type="text"
                      value={clientVigenciaFim}
                      onChange={(e) => setClientVigenciaFim(formatDDMMYYYYVisual(e.target.value))}
                      placeholder="01/02/2026"
                      autoComplete="off"
                      inputMode="numeric"
                    />
                  </label>

                  <label>
                    clientNcmHash (NCM_HASH)
                    <input
                      type="text"
                      value={clientNcmHash}
                      onChange={(e) => setClientNcmHash(e.target.value)}
                      placeholder="sha256 do NCM String"
                      autoComplete="off"
                    />
                  </label>
                </div>

                <div className="fake-label">
                  <span className="fake-title">Ação</span>
                  <div className="form-actions" style={{ paddingTop: 4 }}>
                    <button className="btn ghost" type="button" onClick={doCheckLinguicao} disabled={checking}>
                      <Search size={16} />
                      <span>{checking ? "Checando..." : "Checar atualização"}</span>
                    </button>

                    {/* Novo backend não entrega URL; entrega linguicao direto */}
                    {checkResult?.linguicao && (
                      <button
                        className="btn primary"
                        type="button"
                        onClick={async () => {
                          try {
                            await navigator.clipboard.writeText(checkResult.linguicao);
                            setUploadMsg("String copiado.");
                          } catch {
                            showError("Falha ao copiar NCM String (permissão do navegador).");
                          }
                        }}
                      >
                        <ClipboardCopy size={16} />
                        <span>Copiar NCM String</span>
                      </button>
                    )}
                  </div>
                </div>

                {checkResult?.error && (
                  <div className="state err">
                    <AlertTriangle size={16} />
                    <span>{checkResult.error}</span>
                  </div>
                )}

                {checkResult && !checkResult.error && (
                  <div className={`state ${checkResult.shouldDownload ? "warn" : "ok"}`}>
                    {checkResult.shouldDownload ? <AlertTriangle size={16} /> : <CheckCircle2 size={16} />}
                    <span>
                      {checkResult.shouldDownload ? "Update disponível" : "Sem update"} •{" "}
                      <span className="mono">{checkResult.reason || "-"}</span>
                    </span>
                  </div>
                )}

                {checkResult?.versaoAtual && (
                  <div className="kv compact">
                    <div className="row">
                      <div className="k">VigênciaFim</div>
                      <div className="v">{fmtDate(checkResult.versaoAtual.dtVigenciaFim)}</div>
                    </div>
                    <div className="row">
                      <div className="k">NCM_HASH</div>
                      <div className="v mono" title={checkResult.versaoAtual.ncmHash || ""}>
                        {cutHash(checkResult.versaoAtual.ncmHash)}
                      </div>
                    </div>
                  </div>
                )}

                {checkResult?.linguicao && (
                  <div className="hint">
                    <CheckCircle2 size={14} />
                    <span>
                      String retornado • tamanho: <b>{checkResult.linguicaoChars || checkResult.linguicao.length}</b> chars
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <HelpModal open={helpOpen} onClose={() => setHelpOpen(false)} />
    </MainLayout>
  );
}
